import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get status name
const getStatusName = (status) => {
  return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount) + " "+"sh";
};

// Helper function to get branch name with better error handling
const getBranchName = (branchId, branchesData) => {
  try {
    if (!branchesData || !Array.isArray(branchesData) || !branchId) return 'Unknown';
    const branch = branchesData.find(b => {
      try {
        return parseInt(b?.id) === parseInt(branchId);
      } catch {
        return false;
      }
    });
    return branch ? `${branch.name || ''}`.trim() : 'Unknown';
  } catch (error) {
    console.error('Error getting branch name:', error);
    return 'Unknown';
  }
};

// Helper function to get floor name with better error handling
const getFloorName = (floorId, floorData) => {
  try {
    if (!floorData || !Array.isArray(floorData) || !floorId) return 'Unknown';
    const floor = floorData.find(f => {
      try {
        return parseInt(f?.id) === parseInt(floorId);
      } catch {
        return false;
      }
    });
    return floor ? floor.floor_name || 'Unknown' : 'Unknown';
  } catch (error) {
    console.error('Error getting floor name:', error);
    return 'Unknown';
  }
};

// Helper function to get unit option name with better error handling
const getUnitOptionName = (unitId, unitOptionsData) => {
  try {
    if (!unitOptionsData || !Array.isArray(unitOptionsData) || !unitId) return 'Unknown';
    const unit = unitOptionsData.find(u => {
      try {
        return parseInt(u?.id) === parseInt(unitId);
      } catch {
        return false;
      }
    });
    return unit ? unit.name || 'Unknown' : 'Unknown';
  } catch (error) {
    console.error('Error getting unit option name:', error);
    return 'Unknown';
  }
};

// Helper function to split text into multiple lines for PDF
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF in landscape mode
const drawRoomsTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 12;

  // Column widths for landscape mode
  const colWidths = [10, 25, 35, 35, 25, 25, 25, 25, 30]; // #, Date, Floor, Room Name, Capacity, Bed Charge, Unit Option, Status, Branch

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('landscape');
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(25, 118, 210); // Blue background for header
  doc.setTextColor(255, 255, 255); // White text for header
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Date', 'Floor', 'Room Name', 'Capacity', 'Bed Charge', 'Unit Option', 'Status', 'Branch'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0); // Black text for content

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4; // Account for padding
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage('landscape');
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(25, 118, 210);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
      });
      
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248); // Very light gray for even rows
    } else {
      doc.setFillColor(255, 255, 255); // White for odd rows
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Enhanced PDF export for Rooms with landscape mode
export const exportToPDF = (rooms, floorData = [], unitOptionsData = [], branchesData = [], filters = {}) => {
  try {
    // Create PDF in landscape mode
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // Set document properties
    doc.setProperties({
      title: 'Rooms Report',
      subject: 'Rooms Export',
      author: companyInfo.name,
      keywords: 'rooms, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('ROOMS REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    
    // Report information
    doc.text(`Report Generated: ${new Date().toLocaleDateString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Records: ${rooms.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters.roomtype || filters.capacity || filters.dateFrom || filters.dateTo || filters.status || filters.floor_name_id) {
      doc.text('Filters Applied:', 14, currentY);
      currentY += 6;
      
      if (filters.roomtype) {
        doc.text(`• Room Name: ${filters.roomtype}`, 20, currentY);
        currentY += 6;
      }
      if (filters.capacity) {
        doc.text(`• Capacity: ${filters.capacity}`, 20, currentY);
        currentY += 6;
      }
      if (filters.status) {
        doc.text(`• Status: ${getStatusName(filters.status)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.floor_name_id && floorData.length > 0) {
        const floorName = getFloorName(filters.floor_name_id, floorData);
        doc.text(`• Floor: ${floorName}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateFrom) {
        doc.text(`• From Date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateTo) {
        doc.text(`• To Date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with all required columns using helper functions
    const tableData = rooms.map((room, index) => [
      (index + 1).toString(),
      new Date(room.created_at).toLocaleDateString(),
      getFloorName(room.floor_name_id, floorData),
      room.roomtype,
      room.capacity.toString(),
      formatCurrency(room.bedcharge),
      getUnitOptionName(room.size_unit, unitOptionsData),
      getStatusName(room.status),
      getBranchName(room.branch_id, branchesData)
    ]);
    
    // Draw the table
    drawRoomsTable(doc, tableData, currentY + 5);
    
    // Save the PDF
    const fileName = `rooms_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Rooms PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// Excel export for Rooms with all attributes
export const exportToExcel = (rooms, floorData = [], unitOptionsData = [], branchesData = []) => {
  try {
    // Prepare data for Excel with all attributes using helper functions
    const excelData = rooms.map((room, index) => ({
      '#': index + 1,
      'ID': room.id,
      'Date': new Date(room.created_at).toLocaleDateString(),
      'Floor': getFloorName(room.floor_name_id, floorData),
      'Room Name': room.roomtype,
      'Capacity': room.capacity,
      'Extra Capability': room.exbedcapability === '1' ? 'Yes' : 'No',
      'Default Rate': formatCurrency(room.defaultrate),
      'Bed Charge': formatCurrency(room.bedcharge),
      'Person Charge': formatCurrency(room.personcharge),
      'Room Size': room.roomsize,
      'Size Unit': getUnitOptionName(room.size_unit, unitOptionsData),
      'Bed Number': room.bedsno,
      'Bed Type ID': room.bedstype,
      'Number of Stars': room.number_of_star,
      'Status': getStatusName(room.status),
      'Branch': getBranchName(room.branch_id, branchesData),
      'Room Description': room.roomdescription,
      'Reserve Condition': room.reservecondition,
      'Created Date': new Date(room.created_at).toLocaleString(),
      'Updated Date': new Date(room.updated_at).toLocaleString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rooms');

    // Generate Excel file and download
    const fileName = `rooms_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Rooms Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// CSV export for Rooms with all attributes
export const exportToCSV = (rooms, floorData = [], unitOptionsData = [], branchesData = []) => {
  try {
    // Prepare headers with all attributes
    const headers = ['#', 'ID', 'Date', 'Floor', 'Room Name', 'Capacity', 'Extra Capability', 'Default Rate', 'Bed Charge', 'Person Charge', 'Room Size', 'Size Unit', 'Bed Number', 'Bed Type ID', 'Number of Stars', 'Status', 'Branch', 'Room Description', 'Reserve Condition', 'Created Date', 'Updated Date'];
    
    // Prepare data rows using helper functions
    const csvData = rooms.map((room, index) => [
      index + 1,
      room.id,
      `"${new Date(room.created_at).toLocaleDateString()}"`,
      `"${getFloorName(room.floor_name_id, floorData).replace(/"/g, '""')}"`,
      `"${room.roomtype.replace(/"/g, '""')}"`,
      room.capacity,
      room.exbedcapability === '1' ? 'Yes' : 'No',
      room.defaultrate,
      room.bedcharge,
      room.personcharge,
      room.roomsize,
      `"${getUnitOptionName(room.size_unit, unitOptionsData).replace(/"/g, '""')}"`,
      room.bedsno,
      room.bedstype || '',
      room.number_of_star || '',
      `"${getStatusName(room.status).replace(/"/g, '""')}"`,
      `"${getBranchName(room.branch_id, branchesData).replace(/"/g, '""')}"`,
      `"${(room.roomdescription || '').replace(/"/g, '""')}"`,
      `"${(room.reservecondition || '').replace(/"/g, '""')}"`,
      `"${new Date(room.created_at).toLocaleString()}"`,
      `"${new Date(room.updated_at).toLocaleString()}"`
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `rooms_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Rooms CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};